import React, { useRef, useState } from 'react'
import { NavLink } from 'react-router-dom'
import styled, { css } from 'styled-components'
import { useTranslation } from 'react-i18next'

// Styles
import { mqFrom, mqTo } from '../styles/mediaQueries'
import { hsla } from '../styles/colors'
import { fastTransition } from '../styles/effects'
import { FontWeight } from '../common/models/typography'
import { applyTextStyle } from '../styles/typography'

// Utils
import { paths } from '../constants/routes'
import {
  useAppState,
  useAppDispatch,
  DEFAULT_APP_STATE,
} from '../context/AppContext'
import { emptyStorage } from '../utils/emptyStorage'
import markNotificationsReadService from '../context/services/markNotificationsRead.service'
import {
  filterRequestsByModuleAccess,
  getOwnRequests,
  getCompanyRequests,
} from '../utils/requestsHelper'
import { useModuleAccess } from '../hooks/useModuleAccess'
import useActivityFeed from '../hooks/useActivityFeed'
import { notEmpty } from '../utils/typeGuards'

// Components
import Icon from './icon/Icon'
import Text from './Text'
import SupportFormModal from './form/SupportFormModal'
import NotificationsPopover from './NotificationsPopover'
import { GridRow, GridCol } from './grid'

// Types
import { IconType } from './icon/IconType'
import { NotificationType } from '../common/models/Notification'

interface ILink {
  path: string
  icon: IconType
  title: string
  text: string
  requestAmount: number
}

// The "new content" half of the Me subnav's red dots (Goals/Feedback/
// Journals/Term reviews) — the "action needed" half is already covered by
// userRequests below, since getOwnRequests already includes those types.
const SUBNAV_NOTIFICATION_TYPES = [
  NotificationType.FEEDBACK_RECEIVED,
  NotificationType.FEEDBACK_RECEIVED_EXTERNAL,
  NotificationType.COLLECTION_NEW,
  NotificationType.COLLECTION_NOTE,
  NotificationType.EVALUATION_RECEIVED,
  NotificationType.JOURNAL_NEW_ENTRY,
  NotificationType.GOAL_COMMENT,
]

const NavigationBar: React.FC = () => {
  const { t } = useTranslation()
  const appDispatch = useAppDispatch()
  const {
    user,
    requests,
    theme,
    company,
    userAuthToken,
    notifications,
  } = useAppState()
  const { performanceManagement, onboarding } = useModuleAccess()
  const [supportModal, setSupportModal] = useState<boolean>(false)
  const [notificationsOpen, setNotificationsOpen] = useState<boolean>(false)
  const notificationRef = useRef<HTMLDivElement>(null)

  const { openRequestCount, hasUnreadActivity } = useActivityFeed()
  // Rows the pointer (or focus) has rested on this visit. Flushed once on close
  // instead of marking the whole feed read the moment the panel is opened.
  const seenIdsRef = useRef<Set<string>>(new Set())

  const handleSeen = (notificationIds: string[]) =>
    notificationIds.forEach((id) => seenIdsRef.current.add(id))

  const flushSeen = () => {
    const ids = Array.from(seenIdsRef.current)
    if (ids.length === 0) {
      return
    }
    seenIdsRef.current.clear()
    markNotificationsReadService(ids, userAuthToken || '').catch((e) =>
      console.error(e)
    )
    appDispatch({
      type: 'updateShouldLoadNotifications',
      payload: true,
    })
  }

  const moduleRequests = filterRequestsByModuleAccess(requests, company)

  // The personal page is only meaningful with performance management on, or for an
  // active onboardee. Otherwise hide the "me" entry (the page redirects to /company).
  const showPersonalProfile =
    performanceManagement ||
    (onboarding && !!user?.asCompanyMember?.onboardingId)

  const moreCompanies = user && user.companies?.length > 1

  const userRequests = [
    ...getOwnRequests(moduleRequests),
    ...(user?.asCompanyMember?.isAdmin
      ? getCompanyRequests(moduleRequests)
      : []),
  ]

  const hasUnopenedSubnavNotifications = notifications.some(
    (notification) =>
      !notification.dateOpened &&
      SUBNAV_NOTIFICATION_TYPES.includes(notification.type)
  )

  const links = [
    showPersonalProfile
      ? {
          path: paths.personalProfile,
          icon: 'User',
          title: t('general.users.me'),
          requestAmount:
            userRequests.filter((request) => !request.completed).length +
            (hasUnopenedSubnavNotifications ? 1 : 0),
        }
      : undefined,
    performanceManagement &&
    user?.asCompanyMember?.teams &&
    user.asCompanyMember.teams.length > 0
      ? {
          path: '/team',
          icon: 'Team',
          title: t('general.team'),
        }
      : undefined,
    {
      path: '/company',
      icon: 'Company',
      title: t('general.company'),
    },
  ].filter((link) => notEmpty(link)) as ILink[]

  return (
    <StyledNavigationBar>
      <Wrapper>
        <StyledGridRow alignType="center">
          <StyledGridCol xs={12}>
            <Header>
              <Logo
                type="LogoSummText"
                color={theme.colors.lightest}
                size={64}
              />
            </Header>
            <AnimationWrapper>
              <ContentTop>
                <MobileText
                  textStyle="label"
                  marginLeft={16}
                  color="text72"
                  marginBottom={12}
                >
                  {t('navigationBar.menu')}
                </MobileText>
                <Links>
                  {links.map((link, index) => (
                    <Link
                      activeClassName="selected"
                      to={link.path}
                      key={`nav-link-${index}`}
                    >
                      {link.requestAmount > 0 && <Notification />}
                      <LinkText textStyle="labelHeavy" color="lightest">
                        {link.title}
                      </LinkText>
                      <Chevron
                        type="ChevronRight"
                        color={theme.colors.lightest}
                        size={12}
                      />
                    </Link>
                  ))}
                </Links>
              </ContentTop>
              <ContentBottom>
                <Links>
                  <MobileText
                    textStyle="label"
                    marginLeft={16}
                    color="text72"
                    marginBottom={12}
                  >
                    {t('general.actions.actions')}
                  </MobileText>
                  {moreCompanies && (
                    <Switch to={paths.switchCompanies}>
                      <StyledIcon
                        type="SwitchCompanies"
                        color={theme.colors.lightest}
                        size={20}
                      />
                      <Hover>
                        <Text textStyle="label" color="lightest">
                          {t('switchCompanies.switchCompanies')}
                        </Text>
                      </Hover>
                    </Switch>
                  )}
                  <ActivityButtonLink
                    ref={notificationRef}
                    $active={notificationsOpen}
                    aria-expanded={notificationsOpen}
                    onClick={() => {
                      if (notificationsOpen) flushSeen()
                      setNotificationsOpen(!notificationsOpen)
                    }}
                  >
                    {/* One mark on the bell: work waiting is counted, unread
                        activity is only announced. */}
                    {openRequestCount > 0 ? (
                      <NotificationBadge>{openRequestCount}</NotificationBadge>
                    ) : (
                      hasUnreadActivity && <ActivityDot />
                    )}
                    <StyledIcon
                      type="Notification"
                      color={theme.colors.lightest}
                      size={18}
                    />
                    <Hover>
                      <Text textStyle="label" color="lightest">
                        {t('emails.summary.activity')}
                      </Text>
                    </Hover>
                  </ActivityButtonLink>
                  <ButtonLink onClick={() => setSupportModal(true)}>
                    <StyledIcon
                      type="Support"
                      color={theme.colors.lightest}
                      size={20}
                    />
                    <Hover>
                      <Text textStyle="label" color="lightest">
                        {t('navigationBar.getSupport')}
                      </Text>
                    </Hover>
                  </ButtonLink>
                  <Logout
                    onClick={() => {
                      emptyStorage()
                      appDispatch({
                        type: 'updateState',
                        payload: { ...DEFAULT_APP_STATE },
                      })
                      window.location.href = '/sign-in'
                    }}
                  >
                    <StyledIcon
                      type="Logout"
                      size={20}
                      color={theme.colors.lightest}
                    />
                    <Hover>
                      <Text textStyle="label" color="lightest">
                        {t('general.actions.logOut')}
                      </Text>
                    </Hover>
                  </Logout>
                </Links>
              </ContentBottom>
            </AnimationWrapper>
            {supportModal && (
              <SupportFormModal closeModal={() => setSupportModal(false)} />
            )}
            <NotificationsPopover
              anchor={notificationRef.current}
              isOpen={notificationsOpen}
              onSeen={handleSeen}
              onClose={() => {
                setNotificationsOpen(false)
                flushSeen()
              }}
            />
          </StyledGridCol>
        </StyledGridRow>
      </Wrapper>
    </StyledNavigationBar>
  )
}

const MobileText = styled(Text)`
  font-weight: ${FontWeight.SemiBold};
  text-transform: uppercase;

  ${mqFrom.M`
    display: none;
  `}
`

export const AnimationWrapper = styled.div`
  border-top: 2px solid ${({ theme }) => theme.colors.text72};
  display: flex;
  flex-direction: column;
  height: 100%;
  padding-top: 24px;

  ${mqFrom.M`
    align-items: center;
    flex-direction: row;
    width: 100%;
    border: none;
    padding: 0;
  `}
`

export const StyledNavigationBar = styled.nav`
  z-index: 1000;
  flex-shrink: 0;
  position: fixed;
  top: 0;
  width: 100%;
  background-color: ${({ theme }) =>
    theme.colors.navbg || theme.colors.textLight};

  ${mqFrom.M`
    height: 56px;
    padding: 0 48px;
  `}

  ${mqFrom.L`
    padding: 0 64px;
  `}
`

const Wrapper = styled.div`
  margin: 0 auto;
  width: 100%;
  height: 100%;

  ${mqFrom.L`
    max-width: 1184px;
  `}
`

const StyledGridRow = styled(GridRow)`
  height: 100%;
`

const StyledGridCol = styled(GridCol)`
  display: flex;
  flex-direction: column;
  width: 100%;

  ${mqFrom.M`
    flex-direction: row;
    align-items: center;
  `}
`

const sharedStyles = css`
  cursor: pointer;
  background-color: ${({ theme }) =>
    theme.colors.navbg || theme.colors.textLight};
  align-items: center;
  display: flex;
  height: 56px;
  position: relative;
  padding: 0 24px;

  ${({ theme }) => css`
    ${mqFrom.M`
      &:hover {
        background-color: ${hsla(theme.colors.lightest, 0.27)};
        }
    `}
  `}

  ${mqFrom.M`
    border-radius: 4px;
    padding: 12px 16px;
    justify-content: center;
    height: auto;
  `}
`

// The nav bar's "this one is active" treatment, shared by a selected link and by
// the bell while its popover is open.
const activeNavStyles = css`
  background-color: ${({ theme }) =>
    theme.colors.navselect
      ? hsla(theme.colors.navselect, 0.5)
      : hsla(theme.colors.text, 0.5)};
  box-shadow: inset 2px 2px 5px rgba(0, 0, 0, 0.1);
`

const Header = styled.header`
  display: flex;
  align-items: center;
  padding-left: 16px;
  width: 100%;
  height: 58px;
  align-self: center;
  z-index: 1;
  background-color: ${({ theme }) =>
    theme.colors.navbg || theme.colors.textLight};

  ${mqFrom.M`
    padding: 0;
    margin-right: 24px;
    height: auto;
    width: auto;
  `}
`

const LinkText = styled(Text)`
  ${mqTo.M`
    ${applyTextStyle('title')};
  `}
`

const Logo = styled(Icon)`
  height: 12px;
`

const ContentTop = styled.div`
  width: 100%;
  align-self: center;
`

const ContentBottom = styled.div`
  flex: 1;
  align-items: flex-end;
  display: flex;
  width: 100%;
  align-self: center;
  padding-bottom: 24px;

  ${mqFrom.M`
    align-items: flex-start;
    margin-left: auto;
    padding: 0;
  `}
`

const Links = styled.ul`
  margin: 0;
  padding: 0;
  list-style-type: none;
  width: 100%;

  ${mqFrom.M`
    width: auto;
    display: flex;
    align-items: center;
  `}
`

const StyledIcon = styled(Icon)`
  margin-right: 0;

  ${mqTo.M`
    margin-right: 8px;
  `}
`

const Hover = styled.div`
  background-color: ${({ theme }) =>
    theme.colors.navbg || theme.colors.textLight};

  ${mqFrom.M`
    position: absolute;
    opacity: 0;
    top: calc(100% + 12px);
    padding: 8px 16px;
    border-radius: 4px;
    width: max-content;
    pointer-events: none;
    height: auto;
    transition: opacity ${fastTransition};
  `}
`

const Notification = styled.div`
  border-radius: 50%;
  width: 8px;
  height: 8px;
  background: ${({ theme }) => theme.colors.secondary};
  margin-right: 8px;
`

const bellMarkStyles = css`
  position: absolute;
  background: ${({ theme }) => theme.colors.secondary};
`

// Counts open requests, so it only drops when work is done — never by looking.
const NotificationBadge = styled.div`
  ${bellMarkStyles};
  ${applyTextStyle('labelSmall')};
  top: 4px;
  right: 6px;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: 9px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.lightest};
`

// Unread activity is a nudge, not a to-do list: it says "new", never how much.
const ActivityDot = styled.div`
  ${bellMarkStyles};
  top: 7px;
  right: 9px;
  width: 10px;
  height: 10px;
  border-radius: 50%;
`

const Chevron = styled(Icon)`
  margin-left: auto;

  ${mqFrom.M`
    display: none;
  `}
`

const Logout = styled.li`
  ${sharedStyles};
  position: relative;

  ${mqFrom.M`
    &:hover {
      > ${Hover} {
        opacity: 1;
      }
    }
  `}
`

const Switch = styled(NavLink)`
  cursor: pointer;
  align-items: center;
  display: flex;
  height: 40px;
  position: relative;
  padding: 0 24px;

  ${sharedStyles};

  ${mqFrom.M`
    &:hover {
      > ${Hover} {
        opacity: 1;
      }
    }
  `}
`

const ButtonLink = styled.div`
  ${sharedStyles};
  position: relative;

  ${mqFrom.M`
    &:hover {
      > ${Hover} {
        opacity: 1;
      }
    }
  `}
`

const ActivityButtonLink = styled(ButtonLink)<{ $active: boolean }>`
  ${mqTo.M`
    display: none;
  `}

  ${({ $active }) =>
    $active &&
    css`
      ${activeNavStyles};

      /* An open panel is a state, not a target: hover must not lift the button,
         and its tooltip has nothing left to announce. */
      &:hover {
        ${activeNavStyles};

        > ${Hover} {
          opacity: 0;
        }
      }
    `};
`

const Link = styled(NavLink)`
  &.selected {
    ${activeNavStyles};

    ${mqTo.M`
      &:after {
        content: '';
        height: 100%;
        width: 8px;
        position: absolute;
        top: 0;
        left: 0;
        background-color: white;
        border-radius: 0px 20px 20px 0px;
      }
    `}
  }

  ${sharedStyles};

  ${mqFrom.M`
    &:not(:last-child) {
      margin-right: 16px;
    }
  `}
`

export default NavigationBar
